/*
 * Existencia por sucursal (página de producto y quick add).
 *
 * Datos (sin cambios respecto a la versión anterior):
 *   - /products/<handle>.js            -> variant.available
 *   - /variants/<id>/?section_id=pickup-availability -> sucursales con retiro y su texto de estado
 *   - buildRows / isImmediateAvailable / isVariantAvailable conservan exactamente su lógica.
 *
 * Esta versión solo cambia el pintado (cuadrícula de 2 columnas), los disparadores
 * y las protecciones contra datos viejos, respuestas fuera de orden y errores.
 */
(function () {
  'use strict';

  var NS = 'cmAvailability';
  var prev = window[NS];

  // (e) Si el script se inyecta otra vez (quick add, re-render de sección),
  // se limpia la instancia anterior antes de inicializar esta.
  if (prev && typeof prev.destroy === 'function') {
    try { prev.destroy(); } catch (e) { /* noop */ }
  }

  var SELECTOR = '[data-cm-availability]';
  var MAX_VISIBLE = 8;            // sucursales visibles antes de "Ver las N sucursales"
  var AUTO_RETRY_DELAY = 1500;    // (d) un solo reintento automático
  var PENDING_TIMEOUT = 8000;     // si Dawn nunca confirma la variante, se vuelve a leer el formulario
  var FALLBACK_POLL_MS = 600;     // solo si no existe PUB_SUB_EVENTS.variantChange

  var TEXT_TITLE = 'Existencia por sucursal';
  var TEXT_LOADING = 'Actualizando existencia…';
  var TEXT_ERROR = 'No pudimos consultar la existencia, intenta de nuevo';
  var TEXT_UNAVAILABLE = 'Combinación no disponible';
  var TEXT_RETRY = 'Reintentar';
  var TEXT_SHIP = 'Envío a domicilio';
  var TEXT_NO_STORES = 'Este producto no tiene sucursales con retiro configuradas.';

  var TRUCK_SVG =
    '<svg class="cm-stock__ship-icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M1 4h13v11H1z"/><path d="M14 8h4l4 4v3h-8z"/>' +
      '<circle cx="5.5" cy="18" r="2"/><circle cx="17.5" cy="18" r="2"/>' +
    '</svg>';

  // Caché de /products/<handle>.js compartida entre reinicializaciones (como antes: se pide
  // una vez por producto y no se refresca durante la vida de la página).
  var productCache = (prev && prev.productCache) || {};
  var cleanups = [];
  var intervalId = null;
  var gridCounter = 0;

  /* ───────────── utilidades (sin cambios) ───────────── */

  function normalizeText(t) { return (t || '').replace(/\s+/g, ' ').trim(); }
  function escapeHtml(s) {
    return (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#039;');
  }
  function getRootUrl() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root : '/';
  }

  /* ───────────── datos ───────────── */

  // Mismo endpoint que antes; el handle ahora viene de data-product-handle (R8).
  function getProductData(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    var p = fetch(getRootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function () {
        delete productCache[handle]; // no se guardan fallos en caché
        return null;
      });
    productCache[handle] = p;
    return p;
  }

  // ── Lógica conservada EXACTAMENTE ──
  function isVariantAvailable(product, variantId) {
    if (!product || !product.variants) return null;
    const v = product.variants.find(function (x) { return String(x.id) === String(variantId); });
    return v ? !!v.available : null;
  }

  function getLocationName(item, index) {
    const el =
      item.querySelector('.pickup-availability-list__location') ||
      item.querySelector('.store-availability-list__location') ||
      item.querySelector('[data-pickup-availability-location]') ||
      item.querySelector('h3, h4, strong');
    return normalizeText(el ? el.textContent : '') || ('Sucursal ' + (index + 1));
  }

  function getStatusText(item, locationName) {
    const el =
      item.querySelector('.pickup-availability-list__stock') ||
      item.querySelector('.store-availability-list__item-status') ||
      item.querySelector('[data-pickup-availability-status]') ||
      item.querySelector('[data-store-availability-status]') ||
      item.querySelector('p');
    let d = normalizeText(el ? el.textContent : '');
    if (!d) {
      d = normalizeText(item.textContent);
      if (locationName) d = normalizeText(d.replace(locationName, ''));
    }
    return d;
  }

  function isImmediateAvailable(detail) {
    const t = normalizeText(detail).toLowerCase();
    return t.indexOf('listo en 1 hora') !== -1 || t.indexOf('ready in 1 hour') !== -1 ||
      t.indexOf('generalmente listo') !== -1 || t.indexOf('normalmente está listo') !== -1 ||
      t.indexOf('normalmente esta listo') !== -1;
  }

  function buildRows(html, variantAvailable) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll(
      '.pickup-availability-list__item, .store-availability-list__item, [data-pickup-availability-item], [data-store-availability-item]'
    );

    const rows = [];
    items.forEach(function (item, i) {
      const location = getLocationName(item, i);
      const immediate = variantAvailable && isImmediateAvailable(getStatusText(item, location));
      rows.push({
        location: location,
        label: immediate ? 'Disponible ahora' : 'Sin existencias',
        type: immediate ? 'now' : 'none',
        detail: immediate ? 'Compra en línea y asegura tu compra' : 'Sin existencias en esta sucursal'
      });
    });

    const anyStock = variantAvailable && rows.some(function (r) { return r.type === 'now'; });
    rows.unshift({
      location: '🚚 Envíos',
      label: anyStock ? 'Disponible para envío' : 'Sin existencias',
      type: anyStock ? 'now' : 'none',
      detail: anyStock ? 'Disponible para envío a domicilio 🚚' : 'Sin existencias para envío'
    });
    return rows;
  }
  // ── Fin de la lógica conservada ──

  // Solo diagnóstico (R6): avisa en consola cuando una sucursal trae un texto de estado
  // que no coincide con ningún patrón conocido. No altera el resultado.
  function warnUnknownStatuses(html, variantId) {
    if (!window.console || !console.warn) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll(
      '.pickup-availability-list__item, .store-availability-list__item, [data-pickup-availability-item], [data-store-availability-item]'
    ).forEach(function (item, i) {
      const location = getLocationName(item, i);
      const el =
        item.querySelector('.pickup-availability-list__stock') ||
        item.querySelector('.store-availability-list__item-status') ||
        item.querySelector('[data-pickup-availability-status]') ||
        item.querySelector('[data-store-availability-status]') ||
        item.querySelector('p');
      const raw = normalizeText(el ? el.textContent : '');
      // Texto vacío = la sucursal no ofrece retiro para esta variante (caso conocido).
      if (raw && !isImmediateAvailable(raw)) {
        console.warn('[cm-availability] Texto de estado no reconocido; la sucursal se muestra como "Sin existencias".', {
          sucursal: location,
          variante: String(variantId),
          texto: raw
        });
      }
    });
  }

  /* ───────────── estado por contenedor ───────────── */

  function getState(c) {
    if (!c.__cmAvail) {
      c.__cmAvail = {
        requestId: 0,
        renderedVariantId: null,
        controller: null,
        retryTimer: null,
        pendingTimer: null,
        autoRetried: false,
        status: 'idle',       // idle | pending | loading | ok | error | unavailable
        lastStoreCount: 0,
        expanded: false,
        gridId: 'cm-stock-grid-' + (++gridCounter) + '-' + Date.now().toString(36)
      };
    }
    return c.__cmAvail;
  }

  // Invalida cualquier petición en curso para que su respuesta nunca se pinte.
  function abortCurrent(st) {
    st.requestId++;
    if (st.controller) { try { st.controller.abort(); } catch (e) { /* noop */ } }
    st.controller = null;
    if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
  }

  function clearPending(st) {
    if (st.pendingTimer) { clearTimeout(st.pendingTimer); st.pendingTimer = null; }
  }

  // Variante del formulario del mismo producto (sirve también dentro del quick add).
  function getVariantIdFor(c) {
    const scope = c.closest('product-info') || document;
    const input = scope.querySelector('product-form form input[name="id"]');
    if (input) return input.value ? String(input.value) : null;
    const urlId = new URLSearchParams(location.search).get('variant');
    return urlId || c.getAttribute('data-variant-id') || null;
  }

  function containersIn(scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(SELECTOR));
  }

  /* ───────────── pintado ───────────── */

  function setBusy(c, busy) {
    c.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function headHtml(summaryHtml) {
    return '<div class="cm-stock__head">' +
        '<h2 class="cm-stock__title">' + TEXT_TITLE + '</h2>' +
        (summaryHtml || '') +
      '</div>';
  }

  function renderSkeleton(c) {
    const st = getState(c);
    const count = Math.min(st.lastStoreCount || 4, MAX_VISIBLE);
    let cards = '';
    for (let i = 0; i < count; i++) {
      cards +=
        '<li class="cm-stock__store cm-stock__store--skeleton">' +
          '<span class="cm-stock__sk cm-stock__sk--dot"></span>' +
          '<span class="cm-stock__sk cm-stock__sk--line"></span>' +
          '<span class="cm-stock__sk cm-stock__sk--line cm-stock__sk--short"></span>' +
        '</li>';
    }
    c.innerHTML =
      '<div class="cm-stock is-loading">' +
        headHtml('<p class="cm-stock__summary cm-stock__summary--loading">' + TEXT_LOADING + '</p>') +
        '<div class="cm-stock__ship cm-stock__ship--skeleton" aria-hidden="true">' +
          '<span class="cm-stock__sk cm-stock__sk--icon"></span>' +
          '<span class="cm-stock__ship-text">' +
            '<span class="cm-stock__sk cm-stock__sk--line"></span>' +
            '<span class="cm-stock__sk cm-stock__sk--line cm-stock__sk--short"></span>' +
          '</span>' +
        '</div>' +
        '<ul class="cm-stock__grid" role="list" aria-hidden="true">' + cards + '</ul>' +
      '</div>';
    setBusy(c, true);
  }

  function renderMessage(c, text, withRetry) {
    c.innerHTML =
      '<div class="cm-stock ' + (withRetry ? 'is-error' : 'is-unavailable') + '">' +
        headHtml('') +
        '<div class="cm-stock__message"' + (withRetry ? ' role="alert"' : '') + '>' +
          '<p>' + escapeHtml(text) + '</p>' +
          (withRetry
            ? '<button type="button" class="cm-stock__retry" data-cm-availability-retry>' + TEXT_RETRY + '</button>'
            : '') +
        '</div>' +
      '</div>';
    setBusy(c, false);
  }

  function renderRows(c, rows) {
    const st = getState(c);
    if (!rows || rows.length === 0) {
      renderMessage(c, TEXT_ERROR, true);
      return;
    }

    // buildRows siempre pone "Envíos" en la primera posición; el resto son sucursales.
    const ship = rows[0];
    const stores = rows.slice(1);
    const total = stores.length;                                              // M
    const withStock = stores.filter(function (r) { return r.type === 'now'; }).length; // N
    st.lastStoreCount = total;

    const summary = total > 0
      ? '<p class="cm-stock__summary"><strong>' + withStock + '</strong> de ' + total + ' con existencia</p>'
      : '';

    const shipHtml =
      '<div class="cm-stock__ship is-' + ship.type + '">' +
        TRUCK_SVG +
        '<span class="cm-stock__ship-text">' +
          '<span class="cm-stock__ship-title">' + TEXT_SHIP + '</span>' +
          '<span class="cm-stock__ship-status">' + escapeHtml(ship.label) + '</span>' +
        '</span>' +
      '</div>';

    let body;
    if (total === 0) {
      body = '<p class="cm-stock__note">' + TEXT_NO_STORES + '</p>';
    } else {
      const extra = total > MAX_VISIBLE;
      const cards = stores.map(function (r, i) {
        return '<li class="cm-stock__store is-' + r.type + (i >= MAX_VISIBLE ? ' cm-stock__store--extra' : '') + '">' +
            '<span class="cm-stock__dot" aria-hidden="true"></span>' +
            '<span class="cm-stock__store-name">' + escapeHtml(r.location) + '</span>' +
            '<span class="cm-stock__store-status">' + escapeHtml(r.label) + '</span>' +
          '</li>';
      }).join('');

      body =
        '<ul id="' + st.gridId + '" class="cm-stock__grid' + (extra && st.expanded ? ' is-expanded' : '') + '" role="list">' +
          cards +
        '</ul>' +
        (extra
          ? '<button type="button" class="cm-stock__more" data-cm-availability-more aria-controls="' + st.gridId + '" aria-expanded="' + (st.expanded ? 'true' : 'false') + '"' +
              ' data-label-more="Ver las ' + total + ' sucursales" data-label-less="Ver menos">' +
              (st.expanded ? 'Ver menos' : 'Ver las ' + total + ' sucursales') +
            '</button>'
          : '');
    }

    // Resumen, envío y tarjetas se escriben en una sola asignación (mismo paso).
    c.innerHTML = '<div class="cm-stock">' + headHtml(summary) + shipHtml + body + '</div>';
    setBusy(c, false);
  }

  /* ───────────── flujo de actualización ───────────── */

  function update(c, variantId) {
    const st = getState(c);
    clearPending(st);
    abortCurrent(st);

    const requestId = st.requestId;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    st.controller = controller;
    st.status = 'loading';
    c.setAttribute('data-variant-id', variantId);
    renderSkeleton(c);

    const handle = c.getAttribute('data-product-handle');

    getProductData(handle)
      .then(function (product) {
        if (requestId !== st.requestId) return null;
        if (!product) throw new Error('No se pudo obtener /products/' + handle + '.js');

        const variantAvailable = isVariantAvailable(product, variantId);
        // R8: si no se encuentra la variante NO se asume disponible.
        if (variantAvailable === null) throw new Error('Variante ' + variantId + ' no encontrada en ' + handle);

        return fetch(
          getRootUrl() + 'variants/' + variantId + '/?section_id=pickup-availability',
          {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: controller ? controller.signal : undefined
          }
        )
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status + ' en pickup-availability');
            return r.text();
          })
          .then(function (html) {
            if (requestId !== st.requestId) return; // respuesta vieja: se descarta
            warnUnknownStatuses(html, variantId);
            renderRows(c, buildRows(html, variantAvailable));
            st.status = 'ok';
            st.controller = null;
          });
      })
      .catch(function (err) {
        if (requestId !== st.requestId) return;          // ya hay una petición más nueva
        if (err && err.name === 'AbortError') return;
        console.error('Error disponibilidad:', err);

        // (d) Un único reintento automático; después, botón "Reintentar".
        if (!st.autoRetried) {
          st.autoRetried = true;
          st.retryTimer = setTimeout(function () {
            st.retryTimer = null;
            if (requestId === st.requestId) update(c, variantId);
          }, AUTO_RETRY_DELAY);
          return; // el skeleton sigue visible mientras se reintenta
        }

        st.status = 'error';
        st.controller = null;
        renderMessage(c, TEXT_ERROR, true);
      });
  }

  // Lee la variante actual del formulario y actualiza solo si cambió.
  function sync(c, force) {
    const st = getState(c);
    const id = getVariantIdFor(c);

    if (!id) {
      // (c) Combinación inexistente: nunca dejar datos de la variante anterior.
      clearPending(st);
      abortCurrent(st);
      st.renderedVariantId = null;
      st.status = 'unavailable';
      renderMessage(c, TEXT_UNAVAILABLE, false);
      return;
    }

    if (!force && id === st.renderedVariantId && st.status !== 'pending' && st.status !== 'idle') return;

    st.renderedVariantId = id;
    st.autoRetried = false;
    update(c, id);
  }

  function syncAll() {
    containersIn(document).forEach(function (c) { sync(c, false); });
  }

  // (b) El cliente eligió una opción: se oculta de inmediato el dato anterior.
  function markPending(scope) {
    containersIn(scope).forEach(function (c) {
      const st = getState(c);
      abortCurrent(st);
      clearPending(st);
      st.renderedVariantId = null;
      st.status = 'pending';
      renderSkeleton(c);
      // Respaldo: si Dawn nunca confirma la variante (p. ej. su petición falló),
      // se vuelve a leer el formulario para no dejar el skeleton para siempre.
      st.pendingTimer = setTimeout(function () {
        st.pendingTimer = null;
        if (st.status === 'pending') sync(c, true);
      }, PENDING_TIMEOUT);
    });
  }

  /* ───────────── eventos ───────────── */

  function on(target, type, handler) {
    target.addEventListener(type, handler);
    cleanups.push(function () { target.removeEventListener(type, handler); });
  }

  function onDocumentChange(e) {
    const t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('variant-selects') || t.closest('variant-radios')) {
      markPending(t.closest('product-info') || document);
      return;
    }

    // Dawn actualiza input[name="id"] (y emite "change") cuando ya conoce la variante nueva,
    // también cuando la combinación no existe (valor vacío).
    if (t.matches && t.matches('input[name="id"]') && t.closest('product-form')) {
      const scope = t.closest('product-info') || document;
      containersIn(scope).forEach(function (c) { sync(c, false); });
    }
  }

  function onDocumentClick(e) {
    const t = e.target;
    if (!t || !t.closest) return;

    const retry = t.closest('[data-cm-availability-retry]');
    if (retry) {
      const c = retry.closest(SELECTOR);
      if (!c) return;
      const st = getState(c);
      const id = getVariantIdFor(c);
      if (!id) { sync(c, true); return; }
      st.renderedVariantId = id;
      st.autoRetried = true; // reintento manual: si falla, vuelve a mostrarse el botón
      update(c, id);
      return;
    }

    const more = t.closest('[data-cm-availability-more]');
    if (more) {
      const c2 = more.closest(SELECTOR);
      if (!c2) return;
      const st2 = getState(c2);
      st2.expanded = !st2.expanded;
      const grid = c2.querySelector('.cm-stock__grid');
      if (grid) grid.classList.toggle('is-expanded', st2.expanded);
      more.setAttribute('aria-expanded', st2.expanded ? 'true' : 'false');
      more.textContent = st2.expanded ? more.getAttribute('data-label-less') : more.getAttribute('data-label-more');
    }
  }

  function destroy() {
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
    cleanups.forEach(function (fn) { try { fn(); } catch (e) { /* noop */ } });
    cleanups = [];
    containersIn(document).forEach(function (c) {
      const st = c.__cmAvail;
      if (!st) return;
      clearPending(st);
      const wasInFlight = st.status === 'loading' || st.status === 'pending';
      abortCurrent(st);
      // Lo que estaba cargando se vuelve a pedir en la nueva instancia.
      if (wasInFlight) { st.renderedVariantId = null; st.status = 'idle'; }
    });
  }

  function init() {
    on(document, 'change', onDocumentChange);
    on(document, 'click', onDocumentClick);
    on(document, 'shopify:section:load', syncAll);
    // Eventos que escuchaba la versión anterior (Dawn 15 no los emite; se conservan por compatibilidad).
    on(document, 'variant:change', syncAll);
    on(document, 'variantChange', syncAll);

    // (a) Evento oficial de Dawn. El sondeo solo corre si el evento no existe.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function () { syncAll(); });
      if (typeof unsubscribe === 'function') cleanups.push(unsubscribe);
    } else {
      intervalId = setInterval(syncAll, FALLBACK_POLL_MS);
    }

    syncAll();
  }

  window[NS] = { destroy: destroy, productCache: productCache, refresh: syncAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
